import React, { useContext, useState } from 'react';
import Select from 'react-select';
import _ from 'lodash';
import { ModelContext } from './ModelSource';
import { FormContext, MDForm } from './MDForm';
import { GlobalStateContext } from '../../context/GlobalStateProvider';
import Row from 'antd/lib/grid/row';
import Col from 'antd/lib/grid/col';
import { DeclaredComponent } from '../DynamicComponent';

const operations = {
  equals: (attrValue, expectedValue) => attrValue === expectedValue,
  notequals: (attrValue, expectedValue) => attrValue !== expectedValue,
};

function getTypeCd(condition) {
  // 'if' is the documented default typeCd for conditions, otherwise return lower-case
  return ((condition.typeCd || 'if').toLowerCase( ));
}

export function ConditionalFormChildren({
  attributeRelationship,
  attributes,
  showFields,
  prefix,
}) {
  const { getValueByAttr } = useContext(FormContext);

  function meetsAllConditions(conditionsArr, mustMatchAll = true) {
    let matches = true;
    if (conditionsArr) {
      for (let acIx = 0; acIx < conditionsArr.length; acIx++) {
        const condition = conditionsArr[acIx];
        const condTypeCd = getTypeCd(condition);
        if (condTypeCd === 'or') {
          matches = meetsAllConditions(condition.attributeCondition, false);
        } else if (condTypeCd === 'not') {
          // Check whether the first child is an 'or' to set the mustMatchAll parameter
          const negatedConditions = condition.attributeCondition;
          const matchAll = (( ! negatedConditions) || (getTypeCd(negatedConditions[0]) !== 'or'));
          // Return the negated result of matches all
          matches = ( ! meetsAllConditions(negatedConditions, matchAll));
        } else {
          const operation = operations[condition.operator.toLowerCase()];
          if (operation) {
            matches = operation(
              getValueByAttr(condition.attributeId),
              condition.value
            );
          } else {
            throw new Error('Operator not supported', condition.operator);
          }
        }
        if ((!matches && mustMatchAll) || (matches && !mustMatchAll)) break;
      }
    }
    return matches;
  }

  function meetsAllRelationshipConditions(relationshipArr) {
    let matches = true;

    for (let index = 0; index < relationshipArr.length; index++) {
      const { attributeConditions } = relationshipArr[index];
      matches = meetsAllConditions(attributeConditions);
      if (!matches) break;
    }
    return matches;
  }
  const meetsConditions = meetsAllRelationshipConditions(attributeRelationship);
  if (!meetsConditions) return null;

  const applicationModel = useContext(ModelContext);
  const shownAttributes = attributes.filter(
    (attr) => showFields && showFields.indexOf(attr.id) >= 0
  );
  const formDeclaration = buildFormDeclaration(shownAttributes);

  return (
    <MDForm
      model={applicationModel}
      prefix={prefix}
      productSetupContext={{ attributes: { attributes: shownAttributes } }}
      presentationMetadata={formDeclaration}
    >
      <DeclaredComponent declaration={formDeclaration} />
    </MDForm>
  );
}

function buildFormDeclaration(attributes) {
  return attributes.reduce(
    (result, attribute) => {
      result.Group.content.push({
        Field: {
          attr: attribute.id,
          ...attribute,
        },
      });
      return result;
    },
    { Group: { content: [] } }
  );
}

function getMatchingAttrConditionOptions(attrId, conditions) {
  return conditions.reduce((result, condition) => {
    const condTypeCd = getTypeCd(condition);
    if ((condTypeCd === 'and') || (condTypeCd === 'or') || (condTypeCd === 'not')) {
      result = [
        ...result,
        ...getMatchingAttrConditionOptions(
            attrId,
            condition.attributeCondition
        ),
      ];
    } else {
      if (condition.attributeId === attrId) {
        result.push(condition);
      }
    }
    return result;
  }, []);
}

function getMatchingAttrConditionOptionsRel(attrId, attributes) {
  return attributes.reduce((result, value) => {
    const relationships = value.attributeRelationship;
    for (let index = 0; index < relationships.length; index++) {
      const { attributeConditions } = relationships[index];
      const relevantConditions = getMatchingAttrConditionOptions(
        attrId,
        attributeConditions
      );
      const conditionOptions = relevantConditions.map((c) => ({
        label: c.label || c.value,
        value: c.value,
      }));
      result = [...result, ...conditionOptions];
    }
    return result;
  }, []);
}

let COUNTER = 0;

export function SelectForm({
  entityTypeCdAttr,
  showFields,
  labelColSpan,
  fieldColSpan,
  overrideLabelSpan,
  removeMargin,
  contentTitle,
  lineCodes,
  ...props
}) {
  const formContext = useContext(FormContext);
  const { isDesktop, isMobile, isTablet } = useContext(GlobalStateContext);
  const [domId] = useState(() => `${entityTypeCdAttr}:${COUNTER++}`);
  const metadata = formContext.metadata[entityTypeCdAttr];
  const selectOptionMap = getMatchingAttrConditionOptionsRel(
    entityTypeCdAttr,
    metadata.attributes
  );
  const contextValue = formContext.getValueByAttr(entityTypeCdAttr);
  const currentValue = selectOptionMap.find((v) => v.value === contextValue);
  const selectedMetaFields = Object.keys(showFields).find(
    (v) => v === contextValue
  );

  const attrsToClear = _.uniq(_.flatten(_.values(showFields))).reduce(
    (result, attr) => ({ ...result, [attr]: null }),
    {}
  );

  const handleChange = (opt) => {
    if (!currentValue || opt.value !== currentValue.value) {
      formContext.setValuesByAttr({
        ...attrsToClear,
        [entityTypeCdAttr]: opt.value,
      });
    } else {
      formContext.setValueByAttr(entityTypeCdAttr, opt.value);
    }
  };

  const determineLabelSpan = () => {
    if (labelColSpan !== undefined && overrideLabelSpan === true) {
      return labelColSpan;
    } else if (isTablet || isMobile) {
      return 24;
    } else if (labelColSpan !== undefined) {
      return labelColSpan;
    } else {
      return 4;
    }
  };

  const determineFieldSpan = () => {
    if (isTablet) {
      return 24;
    } else if (isMobile) {
      return 24;
    } else {
      return 12;
    }
  };

  const labelSpan = determineLabelSpan();
  const fieldSpan = determineFieldSpan();

  return (
    <React.Fragment>
      <Row style={{ marginBottom: removeMargin === true ? '' : 24 }}>
        <Col
          gutter={16}
          span={labelSpan}
          style={{
            marginBottom:
              isTablet || (isMobile && labelColSpan === undefined) ? '6px' : '',
          }}
        >
          <label htmlFor={domId}>{contentTitle}</label>
        </Col>
        <Col gutter={16} span={fieldSpan}>
          <Select
            {...props}
            aria-label='Select'
            styles={customStyles}
            openMenuOnFocus
            options={selectOptionMap}
            value={currentValue}
            onChange={handleChange}
            components={{
              IndicatorSeparator: () => null,
            }}
            theme={(theme) => ({
              ...theme,
              borderRadius: 4,
              zIndex: 3,
              colors: {
                ...theme.colors,
                primary25: 'rgb(0, 115, 157, 0.1)',
                primary: 'rgba(0, 115, 157, 0.5)',
                neutral30: 'rgba(0, 115, 157, 0.5)',
              },
            })}
          />
        </Col>
      </Row>
      {metadata.attributes.map((attr) => (
        <ConditionalFormChildren
          {...attr}
          key={attr.id}
          prefix={lineCodes[selectedMetaFields]}
          showFields={showFields[selectedMetaFields]}
          currentValue={currentValue}
        />
      ))}
    </React.Fragment>
  );
}

const customStyles = {
  control: (base) => ({
    ...base,
    '&:hover': {
      cursor: 'pointer',
    },
  }),
  menu: (styles) => ({ ...styles, zIndex: 999 }),
};
